import { useState, useMemo } from "react"
import { X, ChevronRight } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import type { ParasiteRatePoint } from "@/services/parasiteRateService"
import { SpeciesLabel } from "@/components/shared/SpeciesLabel"

// ---------------------------------------------------------------------------
// Species Config
// ---------------------------------------------------------------------------

export const SPECIES_CONFIG = [
  { label: "Any Species", column: "any_positive_rate" as keyof ParasiteRatePoint },
  { label: "Pf", column: "computed_total_pf_positive_rate" as keyof ParasiteRatePoint },
  { label: "Pv", column: "computed_total_pv_positive_rate" as keyof ParasiteRatePoint },
  { label: "Pk", column: "computed_total_pk_positive_rate" as keyof ParasiteRatePoint },
  { label: "Po", column: "computed_total_po_positive_rate" as keyof ParasiteRatePoint },
  { label: "Pm", column: "computed_total_pm_positive_rate" as keyof ParasiteRatePoint },
  { label: "Pan", column: "pan_positive_rate" as keyof ParasiteRatePoint },
]

/**
 * Map from species label to column name for quick lookups during filtering.
 */
export const SPECIES_COLUMN_MAP: Record<string, keyof ParasiteRatePoint> = Object.fromEntries(
  SPECIES_CONFIG.map((s) => [s.label, s.column]),
) as Record<string, keyof ParasiteRatePoint>

// ---------------------------------------------------------------------------
// Age Group Config
// ---------------------------------------------------------------------------

/** The three age-group categories a cluster can be classified into. */
export type AgeGroup = "Under 5" | "Over 5" | "Mixed"

// ---------------------------------------------------------------------------

export interface CountryOption {
  /** ISO3 country code (e.g. "KEN"). Stable identity for this option. */
  iso3: string
  /** Display name for the country (e.g. "Kenya"). */
  name: string
}

export const AGE_GROUP_OPTIONS: readonly AgeGroup[] = ["Under 5", "Over 5", "Mixed"] as const

/**
 * Classify a cluster into an age group from its lower/upper age bounds
 * @param lowerAgeMonths - The rounded lower age bound in months, or null.
 * @param upperAgeMonths - The rounded upper age bound in months, or null.
 * @returns The AgeGroup category
 */
export function classifyAgeGroup(
  lowerAgeMonths: number | null,
  upperAgeMonths: number | null,
): AgeGroup {
  if (lowerAgeMonths == null || upperAgeMonths == null) {
    return "Mixed"
  }

  if (upperAgeMonths < 60) {
    return "Under 5"
  }

  if (lowerAgeMonths >= 60) {
    return "Over 5"
  }

  return "Mixed"
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface FilterListProps {
  /** Currently selected species values. */
  selectedSpecies: string[]
  /** Callback when species selection changes. */
  onSpeciesChange: (species: string[]) => void
  /** All unique method values derived from the loaded dataset. */
  methodOptions: string[]
  /** Currently selected method values. */
  selectedMethods: string[]
  /** Callback when method selection changes. */
  onMethodsChange: (methods: string[]) => void
  /** All unique source_id1 values derived from the loaded dataset (sorted alphanumerically). */
  sourceIdOptions: string[]
  /** Currently selected source IDs to filter by. */
  selectedSourceIds: string[]
  /** Callback when source ID selection changes. */
  onSourceIdsChange: (sourceIds: string[]) => void
  /** All unique map_survey_id values derived from the loaded dataset (sorted alphanumerically). */
  mapSurveyIdOptions: string[]
  /** Currently selected MAP survey IDs to filter by. */
  selectedMapSurveyIds: string[]
  /** Callback when MAP survey ID selection changes. */
  onMapSurveyIdsChange: (mapSurveyIds: string[]) => void
  /** Whether only survey data should be shown (toggle on = survey only). */
  onlySurveyData: boolean
  /** Callback when the survey-only toggle changes. */
  onSurveyDataToggle: (value: boolean) => void
  /** Currently selected age-group categories (Under 5 / Over 5 / Mixed). */
  selectedAgeGroups: AgeGroup[]
  /** Callback when the age-group selection changes. */
  onAgeGroupsChange: (ageGroups: AgeGroup[]) => void
  /** Whether the map is currently in validation-error (warning) mode.*/
  errorModeActive: boolean
  /**All distinct `validation_error` values present in the loaded validation-failure dataset.*/
  validationErrorOptions: string[]
  /** Currently selected validation-error values to narrow the failures by*/
  selectedValidationErrors: string[]
  /** Callback when the validation-error selection changes. */
  onValidationErrorsChange: (validationErrors: string[]) => void
}

export default function FilterList({
  selectedSpecies,
  onSpeciesChange,
  methodOptions,
  selectedMethods,
  onMethodsChange,
  sourceIdOptions,
  selectedSourceIds,
  onSourceIdsChange,
  mapSurveyIdOptions,
  selectedMapSurveyIds,
  onMapSurveyIdsChange,
  onlySurveyData,
  onSurveyDataToggle,
  selectedAgeGroups,
  onAgeGroupsChange,
  errorModeActive,
  validationErrorOptions,
  selectedValidationErrors,
  onValidationErrorsChange,
}: FilterListProps): React.JSX.Element {
  const [sourceIdSearch, setSourceIdSearch] = useState<string>("")
  const [mapSurveyIdSearch, setMapSurveyIdSearch] = useState<string>("")

  function toggleValue(current: string[], value: string, onChange: (values: string[]) => void): void {
    if (current.includes(value)) {
      onChange(current.filter((v) => v !== value))
    } else {
      onChange([...current, value])
    }
  }

  const ANY_SPECIES_LABEL: string = "Any Species"
  const ALL_SPECIES_LABELS: string[] = SPECIES_CONFIG.map((species) => species.label)
  function toggleSpecies(speciesLabel: string): void {
    // "Any Species" is the master switch: select everything or nothing.
    if (speciesLabel === ANY_SPECIES_LABEL) {
      const isCurrentlyChecked: boolean = selectedSpecies.includes(ANY_SPECIES_LABEL)
      onSpeciesChange(isCurrentlyChecked ? [] : [...ALL_SPECIES_LABELS])
      return
    }

    const nextSelection: string[] = selectedSpecies.includes(speciesLabel)
      ? selectedSpecies.filter((label) => label !== speciesLabel)
      : [...selectedSpecies, speciesLabel]

    const realSpeciesLabels: string[] = ALL_SPECIES_LABELS.filter(
      (label) => label !== ANY_SPECIES_LABEL,
    )
    const selectedRealSpecies: string[] = nextSelection.filter(
      (label) => label !== ANY_SPECIES_LABEL,
    )
    const allRealSpeciesSelected: boolean =
      realSpeciesLabels.length > 0 && selectedRealSpecies.length === realSpeciesLabels.length

    const withoutAny: string[] = nextSelection.filter((label) => label !== ANY_SPECIES_LABEL)
    onSpeciesChange(allRealSpeciesSelected ? [ANY_SPECIES_LABEL, ...withoutAny] : withoutAny)
  }

  const filteredSourceIds = useMemo(() => {
    if (!sourceIdSearch.trim()) return sourceIdOptions
    const query = sourceIdSearch.trim().toLowerCase()
    return sourceIdOptions.filter((id) => id.toLowerCase().includes(query))
  }, [sourceIdOptions, sourceIdSearch])

  function addSourceId(sourceId: string): void {
    if (!selectedSourceIds.includes(sourceId)) {
      onSourceIdsChange([...selectedSourceIds, sourceId])
    }
    setSourceIdSearch("")
  }

  function removeSourceId(sourceId: string): void {
    onSourceIdsChange(selectedSourceIds.filter((id) => id !== sourceId))
  }

  const filteredMapSurveyIds = useMemo(() => {
    if (!mapSurveyIdSearch.trim()) return mapSurveyIdOptions
    const query = mapSurveyIdSearch.trim().toLowerCase()
    return mapSurveyIdOptions.filter((id) => id.toLowerCase().includes(query))
  }, [mapSurveyIdOptions, mapSurveyIdSearch])

  function addMapSurveyId(mapSurveyId: string): void {
    if (!selectedMapSurveyIds.includes(mapSurveyId)) {
      onMapSurveyIdsChange([...selectedMapSurveyIds, mapSurveyId])
    }
    setMapSurveyIdSearch("")
  }

  function removeMapSurveyId(mapSurveyId: string): void {
    onMapSurveyIdsChange(selectedMapSurveyIds.filter((id) => id !== mapSurveyId))
  }

  return (
    <div className="max-h-64 overflow-y-auto">
      {errorModeActive && (
        <Collapsible className="mb-3">
          <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground w-full hover:text-foreground transition-colors group">
            <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-90" />
            Validation Error
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="flex flex-col gap-1.5 mt-2 pl-5">
              {validationErrorOptions.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  No validation errors in this dataset.
                </p>
              ) : (
                validationErrorOptions.map(validationError => (
                  <label
                    key={validationError}
                    className="flex items-start gap-2 text-xs cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedValidationErrors.includes(validationError)}
                      onChange={() =>
                        toggleValue(
                          selectedValidationErrors,
                          validationError,
                          onValidationErrorsChange
                        )
                      }
                      className="h-3.5 w-3.5 shrink-0 rounded border-border mt-0.5"
                    />
                    <span className="min-w-0 break-words">{validationError}</span>
                  </label>
                ))
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      <Collapsible className="mb-3">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground w-full hover:text-foreground transition-colors group">
          <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-90" />
          Species
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col gap-1.5 mt-2 pl-5">
            {SPECIES_CONFIG.map(species => (
              <label key={species.label} className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedSpecies.includes(species.label)}
                  onChange={() => toggleSpecies(species.label)}
                  className="h-3.5 w-3.5 shrink-0 rounded border-border"
                />
                <SpeciesLabel label={species.label} />
              </label>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <Collapsible className="mb-3">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground w-full hover:text-foreground transition-colors group">
          <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-90" />
          Age Group
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col gap-1.5 mt-2 pl-5">
            {AGE_GROUP_OPTIONS.map(ageGroup => (
              <label key={ageGroup} className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedAgeGroups.includes(ageGroup)}
                  onChange={() => {
                    if (selectedAgeGroups.includes(ageGroup)) {
                      onAgeGroupsChange(selectedAgeGroups.filter(g => g !== ageGroup))
                    } else {
                      onAgeGroupsChange([...selectedAgeGroups, ageGroup])
                    }
                  }}
                  className="h-3.5 w-3.5 shrink-0 rounded border-border"
                />
                {ageGroup}
              </label>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Method filter (collapsible) */}
      <Collapsible className="mb-3">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground w-full hover:text-foreground transition-colors group">
          <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-90" />
          Method
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col gap-1.5 mt-2 pl-5">
            {methodOptions.map(method => (
              <label key={method} className="flex items-center gap-2 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedMethods.includes(method)}
                  onChange={() => toggleValue(selectedMethods, method, onMethodsChange)}
                  className="h-3.5 w-3.5 shrink-0 rounded border-border"
                />
                {method}
              </label>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Source ID filter (collapsible) */}
      <Collapsible>
        <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground w-full hover:text-foreground transition-colors group">
          <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-90" />
          Source ID
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 pl-5">
            <div>
              <Input
                type="text"
                placeholder={selectedSourceIds.length > 9 ? "Source Limit Reached" : "Source ID..."}
                value={sourceIdSearch}
                onChange={e => setSourceIdSearch(e.target.value)}
                className="h-7 text-xs max-w-35"
                disabled={selectedSourceIds.length > 9}
              />
              {sourceIdSearch.trim() && filteredSourceIds.length > 0 && (
                <div className="mt-1 bg-popover border rounded-md shadow-sm max-h-30 overflow-y-auto">
                  {filteredSourceIds.slice(0, 50).map(id => (
                    <button
                      key={id}
                      type="button"
                      className="w-full text-left px-2 py-1 text-xs hover:bg-accent hover:text-accent-foreground"
                      onClick={() => addSourceId(id)}
                    >
                      {id}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected source IDs as inline tags */}
            {selectedSourceIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedSourceIds.map(id => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-muted rounded"
                  >
                    {id}
                    <button
                      type="button"
                      onClick={() => removeSourceId(id)}
                      className="hover:text-destructive"
                      aria-label={`Remove source ID ${id}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* MAP Survey ID filter (collapsible) */}
      <Collapsible className="mt-3">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground w-full hover:text-foreground transition-colors group">
          <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-90" />
          MAP Survey ID
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 pl-5">
            <div>
              <Input
                type="text"
                placeholder={
                  selectedMapSurveyIds.length > 9 ? "Survey Limit Reached" : " MAP survey ID..."
                }
                value={mapSurveyIdSearch}
                onChange={e => setMapSurveyIdSearch(e.target.value)}
                className="h-7 text-xs max-w-35"
                disabled={selectedMapSurveyIds.length > 9}
              />
              {mapSurveyIdSearch.trim() && filteredMapSurveyIds.length > 0 && (
                <div className="mt-1 bg-popover border rounded-md shadow-sm max-h-32 overflow-y-auto">
                  {filteredMapSurveyIds.slice(0, 50).map(id => (
                    <button
                      key={id}
                      type="button"
                      className="w-full text-left px-2 py-1 text-xs hover:bg-accent hover:text-accent-foreground"
                      onClick={() => addMapSurveyId(id)}
                    >
                      {id}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected MAP survey IDs as inline tags */}
            {selectedMapSurveyIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedMapSurveyIds.map(id => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] bg-muted rounded"
                  >
                    {id}
                    <button
                      type="button"
                      onClick={() => removeMapSurveyId(id)}
                      className="hover:text-destructive"
                      aria-label={`Remove MAP survey ID ${id}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Data Origin filter (collapsible) */}
      <Collapsible className="mt-3 w-40">
        <CollapsibleTrigger className="flex items-center gap-1 text-xs font-medium text-muted-foreground w-full hover:text-foreground transition-colors group">
          <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-90" />
          Data Origin
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex items-center justify-between mt-2 pl-5 max-w-38">
            <label htmlFor="survey-only-toggle" className="text-xs cursor-pointer">
              Only Survey data
            </label>
            <Switch
              id="survey-only-toggle"
              size="sm"
              checked={onlySurveyData}
              onCheckedChange={onSurveyDataToggle}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
